import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { useGetFeedTeamsQuery } from '@/api/feed/getFeedTeams';
import { useGetFeedPlayersQuery } from '@/api/feed/getFeedPlayers';
import { Settings, MoreVertical, Pencil, Search, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SearchableTeamSelect } from '@/components/composite/SearchableTeamSelect';
import { PageContainer } from '@/components/layout/PageContainer';
import { ContentCard } from '@/components/layout/ContentCard';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { generateMLBHeadshotUrl, getPlayerAvatarUrl, type Sport } from '@/utils/jersey-mapping';
import { getPlayerNickname as getNickname } from '@/components/forms/team/teamUtils';
import { isIndividualSport } from '@low6-pickem/sports-config';

export const Route = createFileRoute('/dashboard/sports/$id/players/')({
  component: RouteComponent,
});

interface PlayerData {
  externalId: string;
  teamId: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  position?: string;
  jerseyNumber?: string;
  teamName?: string;
  photoUrl?: string;
  avatarUrl?: string;
}

function RouteComponent() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedTeamId, setSelectedTeamId] = useState<string>('all');
  // Convert route id to lowercase for API (NFL -> nfl, NBA -> nba)
  const leagueId = id.toLowerCase();
  const isIndividual = isIndividualSport(id);

  const { data: teams = [], isLoading: teamsLoading } = useGetFeedTeamsQuery(leagueId);
  const { data: players = [], isLoading: playersLoading } = useGetFeedPlayersQuery(
    selectedTeamId !== 'all' ? selectedTeamId : undefined
  );

  const filteredPlayers = useMemo(() => {
    if (!searchQuery.trim()) return players;

    const query = searchQuery.toLowerCase();
    return players.filter(
      player =>
        player.displayName.toLowerCase().includes(query) ||
        player.position?.toLowerCase().includes(query) ||
        player.jerseyNumber?.toLowerCase().includes(query) ||
        player.externalId.toLowerCase().includes(query)
    );
  }, [players, searchQuery]);

  const handleEdit = (player: PlayerData) => {
    navigate({
      to: '/dashboard/sports/$id/players/$playerId',
      params: { id, playerId: player.externalId },
    });
  };

  // Get full team name: "Market Name" (e.g., "Texas A&M Aggies")
  // Falls back to just name if market is not available
  const getFullTeamName = (team: { name: string; market?: string }) => {
    if (team.market && team.market !== team.name) {
      return `${team.market} ${team.name}`;
    }
    return team.name;
  };

  const getTeamName = (teamId: string) => {
    const team = teams.find(t => t.externalId === teamId);
    if (!team) return 'Unknown Team';
    return getFullTeamName(team);
  };

  const getTeamAbbreviation = (teamId: string) => {
    const team = teams.find(t => t.externalId === teamId);
    return team?.abbreviation || null;
  };

  const getHeadshotUrl = (player: PlayerData) => {
    if (leagueId !== 'mlb') return player.photoUrl || null;
    const teamAbbr = getTeamAbbreviation(player.teamId);
    if (!teamAbbr || !player.firstName || !player.lastName) return player.photoUrl || null;
    return generateMLBHeadshotUrl(teamAbbr, player.firstName, player.lastName);
  };

  const getAvatarUrl = (player: PlayerData) => {
    if (player.avatarUrl) return player.avatarUrl;
    const teamAbbr = getTeamAbbreviation(player.teamId);
    if (!teamAbbr) return null;
    return getPlayerAvatarUrl(teamAbbr, sportName as Sport, player.jerseyNumber);
  };

  const isLoading = teamsLoading || playersLoading;
  const sportName = id.toUpperCase();

  return (
    <PageContainer>
      <Breadcrumbs
        overrides={{ sports: 'Sports', [id]: id.toUpperCase() }}
        pathOverrides={{ sports: `/dashboard/sports/${id}` }}
      />

      <div className="flex gap-4 items-center mb-6 flex-wrap">
        <div className="flex items-center gap-2">
          <Settings className="h-5 w-5 text-gray-500" />
          <h1 className="text-2xl font-semibold">
            {sportName} {'>'} Players
          </h1>
        </div>

        <div className="flex-1 flex gap-2 items-center">
          <SearchableTeamSelect
            teams={teams}
            value={selectedTeamId}
            onValueChange={setSelectedTeamId}
            placeholder={isIndividual ? 'All Tournaments' : 'All Teams'}
            className="w-[280px]"
            getDisplayName={team => getFullTeamName(team)}
            getSearchText={team =>
              `${team.market || ''} ${team.name} ${team.abbreviation || ''}`.trim()
            }
            leadingOptions={[
              { value: 'all', label: isIndividual ? 'All Tournaments' : 'All Teams' },
            ]}
          />

          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* TODO: Add Player button hidden until backend endpoint is available */}
      </div>

      <ContentCard>
        <Table>
          <TableHeader className="[&_tr]:border-b-0">
            <TableRow>
              <TableHead className="bg-gray-100 dark:bg-gray-800 rounded-l-xl font-bold">
                ID
              </TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Headshot</TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Name</TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Nick Name</TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">League</TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">
                {isIndividual ? 'Tournament' : 'Team'}
              </TableHead>
              {!isIndividual && (
                <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Number</TableHead>
              )}
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">
                {isIndividual ? 'Country' : 'Position'}
              </TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Avatar</TableHead>
              <TableHead className="text-right bg-gray-100 dark:bg-gray-800 rounded-r-xl font-bold">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-10 w-10 rounded-full" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-12" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-8 w-8 rounded" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-8 ml-auto" />
                  </TableCell>
                </TableRow>
              ))
            ) : selectedTeamId === 'all' ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8">
                  <div className="flex flex-col items-center gap-2">
                    <Users className="h-12 w-12 text-gray-400" />
                    <p className="text-gray-600 dark:text-gray-400">
                      {isIndividual
                        ? 'Please select a tournament to view players'
                        : 'Please select a team to view players'}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredPlayers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8">
                  <div className="flex flex-col items-center gap-2">
                    <Users className="h-12 w-12 text-gray-400" />
                    <p className="text-gray-600 dark:text-gray-400">
                      {searchQuery
                        ? 'No players found matching your search'
                        : 'No players found for this team'}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredPlayers.map((player: PlayerData) => (
                <TableRow key={player.externalId}>
                  <TableCell className="font-medium">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="block max-w-[60px] truncate">{player.externalId}</span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{player.externalId}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell>
                    <PlayerHeadshot url={getHeadshotUrl(player)} name={player.displayName} />
                  </TableCell>
                  <TableCell className="font-medium">{player.displayName}</TableCell>
                  <TableCell>{getNickname(player.displayName)}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{sportName}</Badge>
                  </TableCell>
                  <TableCell>{getTeamName(player.teamId)}</TableCell>
                  {!isIndividual && (
                    <TableCell>
                      {player.jerseyNumber ? (
                        <Badge variant="outline" className="font-mono">
                          {player.jerseyNumber}
                        </Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    {player.position ? (
                      <Badge variant="outline">{player.position}</Badge>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const avatar = getAvatarUrl(player);
                      return avatar ? (
                        <img
                          src={avatar}
                          alt={player.displayName}
                          className="h-8 w-8 rounded object-cover"
                          onError={e => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="h-8 w-8 rounded bg-teal-500 flex items-center justify-center">
                          <Users className="h-4 w-4 text-white" />
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(player)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ContentCard>
    </PageContainer>
  );
}

function PlayerHeadshot({ url, name }: { url: string | null; name: string }) {
  const [failed, setFailed] = useState(false);

  if (url && !failed) {
    return (
      <img
        src={url}
        alt={name}
        className="h-10 w-10 rounded-full object-cover"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
      <Users className="h-5 w-5 text-gray-400" />
    </div>
  );
}
